<template>
  <StepLayout
    :on-back="onBack"
    :on-save="onSave"
    :is-busy-on-save="isBusyOnSave"
    :on-next="onNext"
    :is-busy-on-next="isBusyOnNext"
    :is-disabled="isDisabled"
    :uhoh="uhoh"
    :closeUhoh="closeUhoh"
    :uhohRoute="uhohRoute"
    helpscout-article="6568745688645025e1e2cfce"
    step-navigation-button-data-ec="bulk_send_import_data_page"
  >
    <template v-slot:title>
      {{ $t('bulkSend.steps.importData.title', { name }) }}
    </template>

    <p class="font-bold">{{ $t('bulkSend.steps.importData.step1') }}</p>
    <p>{{ $tc('bulkSend.steps.importData.step1Info', bulkSend.templates.length) }}</p>
    <BaseButton
      v-if="!csvDownloaded"
      size="small"
      variant="tertiary"
      @click.native="downloadTemplate"
      data-ec="bulk_send_import_data_page"
      data-et="download_csv_template_button"
    >
      <span>{{ $t('bulkSend.steps.importData.downloadButtonText') }}</span>
    </BaseButton>
    <div
      v-else
      class="flex gap-base items-center"
    >
      <SuccessIndicator />
      <b class="text-{#808080} text-12">{{ $t('bulkSend.steps.importData.csvDownloaded') }}</b>
      <div class="h-base border border-solid border-{#808080}" />
      <BaseButton
        class="!px-0 !no-underline"
        size="small"
        variant="invisible"
        @click.native="downloadTemplate"
        data-ec="bulk_send_import_data"
        data-et="download_again_button"
      >
        <span>{{ $t('bulkSend.steps.importData.downloadAgain') }}</span>
      </BaseButton>
    </div>

    <hr />

    <p class="font-bold">{{ $t('bulkSend.steps.importData.step2') }}</p>
    <p class="mb-0">{{ $t('bulkSend.steps.importData.step2Info') }}</p>
    <div
      v-if="importedFilename"
      class="flex gap-base items-center mt-base"
    >
      <SuccessIndicator v-if="!uploadRequired && importValid" />
      <FailIndicator v-else />
      <b class="text-{#808080} text-12">
        {{ importedFilename }}
      </b>
      <div class="h-base border border-solid border-{#808080}" />
      <BaseButton
        v-if="!uploadRequired && importValid"
        class="!px-0 !no-underline"
        size="small"
        variant="invisible"
        @click.native="clearImportedFilename"
      >
        <span>{{ $t('bulkSend.steps.importData.replaceUpload') }}</span>
      </BaseButton>
      <b
        v-else
        class="text-{#808080} text-12"
      >
        {{ requiresCsv ? $t('bulkSend.steps.importData.csvOutdated') : $t('bulkSend.steps.importData.importInvalid') }}
      </b>
    </div>

    <TWFilePicker
      v-if="uploadRequired || !importValid"
      class="mt-base"
      accept=".csv"
      :uploaderMessage="$t('bulkSend.steps.importData.filePickerText')"
      @add="addCsv"
      data-ec=""
      data-et=""
    >
      <template v-slot:files>
        <VFileList
          class="mb-base w-fit"
          :value="csv ? [csv] : []"
          @remove="clearCsv"
        />
      </template>
    </TWFilePicker>
  </StepLayout>
</template>

<script>
// @ts-check
import { computed, defineComponent, onActivated, ref, watch } from 'vue';
import kebabCase from 'lodash/kebabCase';
import { useRoute } from 'vue-router/composables';
import { useActions, useGetters } from 'vuex-composition-helpers/dist';

import download from '@/utils/download';
import { useBulkSendStepsNavigation } from '@/composables/bulkSend/steps';
import useBulkSendUhoh from '@/composables/bulkSend/uhoh';

import VFileList from '@/components/deprecated/molecules/FileList.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import FailIndicator from '@/components/library/indicators/FailIndicator.vue';
import SuccessIndicator from '@/components/library/indicators/SuccessIndicator.vue';
import StepLayout from '@/components/ui/bulkSend/StepLayout.vue';
import TWFilePicker from '@/components/tailwind/FilePicker.vue';

export default defineComponent({
  name: 'ImportDataPage',
  components: {
    TWFilePicker,
    VFileList,
    StepLayout,
    BaseButton,
    SuccessIndicator,
    FailIndicator,
  },
  setup() {
    const route = useRoute();

    // get bulk send data
    /** @type {import('@/store/modules/bulkSend').Getters} */
    const { bulkSend } = useGetters({ bulkSend: 'bulkSend/bulkSend' });
    const { fetchTheBulkSend } = useActions({
      fetchTheBulkSend: 'bulkSend/fetchTheBulkSend',
    });

    const requiresCsv = computed(() => bulkSend.value.import_status === 'requires-csv');
    const importValid = computed(() => bulkSend.value.import_valid);

    const importedFilename = ref(bulkSend.value.imported_filename);
    function clearImportedFilename() {
      importedFilename.value = null;
    }
    watch(bulkSend, (updatedBulksend) => {
      importedFilename.value = updatedBulksend.imported_filename;
    });

    const uploadRequired = computed(() => requiresCsv.value || !importedFilename.value);

    // initialise local state
    /** @type {import('vue').Ref<File>} */
    const csv = ref();
    /** @param {FileList} files */
    function addCsv(files) {
      csv.value = files.item(0);
    }
    function clearCsv() {
      csv.value = undefined;
    }

    const csvDownloaded = ref(false);

    const updateValue = computed(() => {
      if (!uploadRequired.value && !(csv.value instanceof File)) {
        return null;
      }
      const data = new FormData();
      data.append('data', csv.value);
      return data;
    });

    const isValid = computed(() => !uploadRequired.value || csv.value instanceof File);

    onActivated(() => {
      fetchTheBulkSend(route.params.fingerprint);
      clearCsv();
    });

    const {
      uhoh,
      closeUhoh,
      uhohRoute,
      onError,
    } = useBulkSendUhoh();

    const {
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
    } = useBulkSendStepsNavigation(
      'bulkSend/uploadImport',
      updateValue,
      isValid,
      onError,
    );

    return {
      bulkSend,
      requiresCsv,
      importValid,
      csv,
      addCsv,
      clearCsv,
      csvDownloaded,
      importedFilename,
      clearImportedFilename,
      uploadRequired,
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
      uhoh,
      closeUhoh,
      uhohRoute,
    };
  },
  computed: {
    name() {
      return this.bulkSend.name;
    },
  },
  methods: {
    async downloadTemplate() {
      await download({
        url: `bulk-sends/${this.bulkSend.fingerprint}/import-template`,
        fileName: kebabCase(`${this.bulkSend.name}-template`),
      });
      this.csvDownloaded = true;
    },
  },
});
</script>
